
import React from "react";
import { motion } from "framer-motion";
import {
  Award,
  BookOpen,
  MessageCircle,
  Quote,
  Sparkles,
} from "lucide-react";

const PrincipalDesk = () => {
  return (
    <section className="relative isolate w-full overflow-hidden bg-[#FFFCF8] py-8 sm:py-10 lg:py-12">
      {/* BACKGROUND DECORATIONS */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-28 top-16 h-72 w-72 rounded-full bg-red-100/60 blur-3xl" />

        <div className="absolute -right-28 bottom-0 h-80 w-80 rounded-full bg-amber-100/60 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />

        <div className="absolute -right-24 top-10 h-56 w-56 rounded-full border border-red-200/50" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-5 sm:px-8 lg:px-12 xl:px-16">
        {/* SECTION HEADING */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-red-800 shadow-sm">
            <Sparkles size={13} className="text-amber-500" />
            A Message of Inspiration
          </span>

          <h2 className="mt-4 font-serif text-[30px] font-black leading-tight text-slate-950 sm:text-[38px] lg:text-[46px]">
            Message from the{" "}
            <span className="relative inline-block text-red-800">
              Principal
              <span className="absolute -bottom-2 left-0 h-1 w-full rounded-full bg-gradient-to-r from-red-700 to-amber-400" />
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-[13px] leading-relaxed text-slate-500 sm:text-[15px]">
            Inspiring every learner to achieve academic excellence,
            develop strong values and embrace new opportunities.
          </p>
        </motion.div>

        {/* MAIN TWO-COLUMN LAYOUT */}
        <div className="grid items-start gap-8 lg:grid-cols-[0.88fr_1.12fr] lg:gap-10">
          {/* LEFT SIDE - PRINCIPAL IMAGE */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="relative mx-auto w-full max-w-[600px]"
          >
            {/* GOLDEN DECORATION */}
            <div className="pointer-events-none absolute -left-3 -top-3 h-32 w-32 rounded-[30px] border-[8px] border-amber-300 sm:-left-4 sm:-top-4" />

            {/* RED DECORATION */}
            <div className="pointer-events-none absolute -bottom-4 -right-3 h-32 w-32 rounded-[30px] bg-red-800 sm:-bottom-4 sm:-right-4" />

            {/* CLEAN IMAGE */}
            <div className="relative z-10 overflow-hidden rounded-[28px] border-[10px] border-white bg-white shadow-[0_20px_50px_rgba(100,25,25,0.15)]">
              <div className="group relative h-[360px] overflow-hidden sm:h-[460px] lg:h-[560px] xl:h-[590px]">
                <img
                  src="/director.png"
                  alt="Principal of St. Xavier's Public School"
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.025]"
                />
              </div>
            </div>

            {/* IMAGE CAPTION - OUTSIDE PHOTO */}
            <div className="relative z-20 mx-4 -mt-1 rounded-b-2xl border-x border-b border-red-100 bg-white px-5 py-4 shadow-sm sm:mx-7">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-red-800">
                School Leadership
              </p>

              <h3 className="mt-1 font-serif text-xl font-bold text-slate-950">
                Principal&apos;s Desk
              </h3>

              <p className="mt-1 text-xs text-slate-500">
                St. Xavier&apos;s Public School, Bela
              </p>
            </div>
          </motion.div>

          {/* RIGHT SIDE - MESSAGE */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            {/* GREETING */}
            <div className="relative overflow-hidden rounded-2xl border border-red-100 bg-white px-5 py-4 shadow-sm">
              <div className="absolute bottom-0 left-0 top-0 w-1 bg-red-800" />

              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
                  <MessageCircle size={21} />
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-red-700">
                    Principal&apos;s Message
                  </p>

                  <h3 className="mt-1 font-serif text-lg font-bold text-slate-950 sm:text-xl">
                    Dear Parents and Students,
                  </h3>
                </div>
              </div>
            </div>

            {/* MESSAGE CONTENT */}
            <div className="mt-5 space-y-4 text-[13px] leading-[1.85] text-slate-600 sm:text-[14px]">
              <p>
                Down through the years,{" "}
                <strong className="font-bold text-red-800">
                  St. Xavier&apos;s School
                </strong>{" "}
                has been a torchbearer and trendsetter,
                responding to the demands of modern science,
                technology and education. We nurture our
                students with knowledge, determination and
                self-discipline, encouraging them to
                confidently discover their own path.
              </p>

              <p>
                We believe in the{" "}
                <strong className="text-slate-900">
                  all-round development of every learner
                </strong>{" "}
                through a strong academic curriculum and
                meaningful co-curricular activities.
                Our goal is to provide a supportive
                school environment that encourages
                continuous growth, professional
                development and improvement.
              </p>
            </div>

            {/* MAIN QUOTE */}
            <div className="relative my-5 overflow-hidden rounded-2xl bg-gradient-to-r from-red-800 to-red-700 px-6 py-5 text-center shadow-[0_10px_30px_rgba(127,29,29,0.15)]">
              <div className="pointer-events-none absolute -right-5 -top-7 h-24 w-24 rounded-full border-[12px] border-white/10" />

              <Quote
                size={23}
                className="relative mx-auto mb-2 text-amber-300"
              />

              <p className="relative font-serif text-lg font-bold italic text-white sm:text-xl">
                “I see it, I get it, I can do it.”
              </p>
            </div>

            {/* CLOSING PARAGRAPH */}
            <div className="space-y-4 text-[13px] leading-[1.85] text-slate-600 sm:text-[14px]">
              <p>
                St. Xavier&apos;s School continues to
                strengthen its academic planning,
                school organisation, staff development
                and student guidance. Through debates,
                quiz competitions, cultural programmes
                and extracurricular activities, we
                encourage students to discover their
                potential and develop essential life
                skills.
              </p>

              <p>
                Innovation, determination and noble
                values are the foundations of a
                meaningful future. We aim to prepare
                confident, responsible citizens who
                contribute positively to society.
              </p>
            </div>

            {/* TAGORE INSPIRATION */}
            <div className="mt-5 rounded-xl border-l-4 border-amber-400 bg-white px-5 py-4 shadow-sm">
              <div className="flex items-start gap-3">
                <BookOpen
                  size={19}
                  className="mt-0.5 shrink-0 text-amber-600"
                />

                <div>
                  <p className="font-serif text-[13px] italic leading-[1.75] text-slate-700 sm:text-sm">
                    “Where the mind is without fear
                    and the head is held high,
                    where knowledge is free...”
                  </p>

                  <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-red-800">
                    — Rabindranath Tagore
                  </p>
                </div>
              </div>
            </div>

            {/* SUBTLE CLOSING LABEL */}
            <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-slate-500">
              <Award size={17} className="text-amber-600" />
              Inspiring Excellence Through Education
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PrincipalDesk;
